import React, { Component } from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import GamePlayerList from 'components/GamePlayerList';
import MessageForm, { MESSAGE_TYPE_CHAT } from 'components/MessageForm';
import { getUserData } from 'utils/graphQLFunctions';
import insertionSortUnique from 'utils/insertionSortUnique';
import { chatMessageShape, errorShape } from 'utils/shapes';
import fragments from 'utils/graphql/fragments';
import './GameChat.css';

const MAX_CHAT_MESSAGES = 500;
const MAX_ALERTS = 50;
const CHAT_SUBMIT_DELAY_MS = 1000;

class GameChat extends Component {
  constructor(props) {
    super(props);

    this.state = {
      message: '',
      alerts: []
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.scrollToBottom = this.scrollToBottom.bind(this);
    this.handleAddPlayers = this.handleAddPlayers.bind(this);
    this.handleRemovePlayers = this.handleRemovePlayers.bind(this);
    this.nextAlertKey = 1;
    this.chatSubmitTimer = 0;

    this.unsubscribeFromChatUpdates = () => {};
  }

  handleChange(event) {
    this.setState({
      message: event.target.value
    });
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' && event.shiftKey === false) {
      this.handleSubmit(event);
    }
  }

  handleSubmit(event) {
    event.preventDefault();

    if (Date.now() < this.chatSubmitTimer) {
      return;
    }

    const message = this.state.message.replace(/[\n\r]/g, '').trim();
    if (message.length > 0) {
      this.props.makeChatMessage(message);
      this.chatSubmitTimer = Date.now() + CHAT_SUBMIT_DELAY_MS;
    }

    this.setState({ message: '' });
  }

  scrollToBottom() {
    const scrollHeight = this.listContainer.scrollHeight;
    const height = this.listContainer.clientHeight;
    const maxScrollTop = scrollHeight - height;
    this.listContainer.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
  }

  componentDidMount() {
    this.unsubscribeFromChatUpdates = this.props.subscribeToChatUpdates();
    this.scrollToBottom();
  }

  componentWillUnmount() {
    this.unsubscribeFromChatUpdates();
    this.unsubscribeFromChatUpdates = () => {};
  }

  componentWillUpdate() {
    const c = this.listContainer;
    this.shouldScrollBottom = c.scrollTop + c.offsetHeight === c.scrollHeight;
  }

  componentDidUpdate() {
    if (this.shouldScrollBottom) {
      this.listContainer.scrollTop = this.listContainer.scrollHeight;
    }
  }

  addAlert(message) {
    this.setState((prevState, props) => {
      const msgs = props.chatData && props.chatData.chatMessages;
      let trim = Math.max(0, prevState.alerts.length + 1 - MAX_ALERTS);
      let newId = 0;
      let updatedAlerts = [];
      if (msgs && msgs.length) {
        // Attach the alert to the latest message
        newId = msgs[msgs.length - 1].id;
        const oldestId = msgs[0].id;
        for (; trim < prevState.alerts.length; ++trim) {
          const alert = prevState.alerts[trim];
          if (!alert.id) {
            // Update any previously-unattached alerts
            updatedAlerts.push(Object.assign({}, alert, { id: oldestId - 1 }));
          } else if (alert.id < oldestId - 1) {
            // Allow this alert to be trimmed
          } else {
            break;
          }
        }
      }

      const newAlerts = updatedAlerts.concat(prevState.alerts.slice(trim), {
        id: newId,
        message: message,
        key: this.nextAlertKey++
      });
      return { alerts: newAlerts };
    });
  }

  handleAddPlayers(users) {
    users.forEach(user =>
      this.addAlert(user.displayName + ' has started playing.')
    );
  }

  handleRemovePlayers(users) {
    //users.forEach(user =>
    //  this.addAlert(user.displayName + ' has stopped playing.')
    //);
  }

  renderAlert(alert) {
    return (
      <li className="chat-alert" key={`alert-${alert.key}`}>
        <span>{alert.message}</span>
      </li>
    );
  }

  renderMessage(msg) {
    return (
      <li className="chat-message" key={`msg-${msg.id}`}>
        <span className="chat-author-name">{msg.author.displayName}</span>{' '}
        {msg.message}
      </li>
    );
  }

  render() {
    const { chatData, isTournament, gameName, tournamentId } = this.props;

    let content = [];
    if (chatData && !chatData.loading && !chatData.error) {
      const messages = chatData.chatMessages;
      const alerts = this.state.alerts;
      let msgIndex = 0;
      let alertIndex = 0;
      while (true) {
        const msg = msgIndex < messages.length && messages[msgIndex];
        const alert = alertIndex < alerts.length && alerts[alertIndex];
        if (msg && (!alert || msg.id <= alert.id)) {
          content.push(this.renderMessage(msg));
          msgIndex++;
        } else if (alert) {
          content.push(this.renderAlert(alert));
          alertIndex++;
        } else {
          break;
        }
      }
    }

    return (
      <div className="game-chat-container">
        {!isTournament && (
          <div className="chat-header">
            <GamePlayerList
              gameName={gameName}
              tournamentId={tournamentId}
              onAddPlayers={this.handleAddPlayers}
              onRemovePlayers={this.handleRemovePlayers}
            />
          </div>
        )}
        <div
          className="chat-list-container"
          ref={div => {
            this.listContainer = div;
          }}
        >
          <ul className="chat-list">{content}</ul>
        </div>
        <div className="feed-comment-container game-chat-form">
          <MessageForm
            onSubmit={this.handleSubmit}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            message={this.state.message}
            placeholder={MESSAGE_TYPE_CHAT}
          />
        </div>
      </div>
    );
  }
}

GameChat.propTypes = {
  isTournament: PropTypes.bool,
  room: PropTypes.string.isRequired,
  gameName: PropTypes.string.isRequired,
  tournamentId: PropTypes.string,
  chatData: PropTypes.shape({
    chatMessages: PropTypes.arrayOf(chatMessageShape),
    loading: PropTypes.bool,
    error: errorShape
  }),
  userData: PropTypes.shape({
    user: PropTypes.shape({
      email: PropTypes.string,
      userName: PropTypes.string,
      displayName: PropTypes.string
    }),
    loading: PropTypes.bool,
    error: errorShape
  })
};

const QUERY_GET_CHAT_MESSAGES = gql`
  query MyGetChatMessages($input: GetChatMessages!) {
    chatMessages(input: $input) {
      ...ChatMessageFields
    }
  }
  ${fragments.chatMessage}
`;

const MUTATION_MAKE_CHAT_MESSAGE = gql`
  mutation MyMakeChatMessage($input: MakeChatMessage!) {
    makeChatMessage(input: $input) {
      ...ChatMessageFields
    }
  }
  ${fragments.chatMessage}
`;

const SUBSCRIPTION_CHAT_UPDATES = gql`
  subscription MyChatUpdates($input: ChatUpdatesInput!) {
    chatUpdates(input: $input) {
      ...ChatMessageFields
    }
  }
  ${fragments.chatMessage}
`;

export default compose(
  getUserData({
    name: 'userData'
  }),
  graphql(QUERY_GET_CHAT_MESSAGES, {
    name: 'chatData',
    skip: props => !props.room,
    options: props => ({
      variables: {
        input: {
          room: props.room,
          count: 100,
          durationSec: 3600
        }
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps, chatData }) => ({
      chatData,
      subscribeToChatUpdates: () => {
        return chatData.subscribeToMore({
          document: SUBSCRIPTION_CHAT_UPDATES,
          variables: { input: { room: ownProps.room } },
          updateQuery: (prev, { subscriptionData: { data: newMsg } }) => {
            if (!newMsg || newMsg.room !== ownProps.room) {
              return prev;
            }
            let result = cloneDeep(prev); // NOTE: Do not modify prev
            let modified = false;

            const msgs = result.chatMessages;
            if (insertionSortUnique(msgs, newMsg, m => m.id)) {
              modified = true;
              let overflow = msgs.length - MAX_CHAT_MESSAGES;
              if (overflow > 0) {
                msgs.splice(0, overflow);
              }
            }

            return modified ? result : prev;
          }
        });
      }
    })
  }),
  graphql(MUTATION_MAKE_CHAT_MESSAGE, {
    props: ({ ownProps, mutate }) => ({
      makeChatMessage: message =>
        mutate({
          variables: { input: { room: ownProps.room, message: message } }
        })
    })
  })
)(GameChat);



// WEBPACK FOOTER //
// ./src/components/GameChat/index.js